import { createSlice } from '@reduxjs/toolkit';
import ApiClientConnector from 'src/api/ApiClientConnector';
import { generateTransferRoutes } from 'src/store/reservations/actions/transfers/TransferRouteCalculator';
import { deriveTransferPassengersFromReservation } from '../../actions/transfers/TransferActions';
import { defaultTransferPassengers } from 'src/utils/constants';

const ManageTransferSlice = createSlice({
    name: 'transferReducer',
    initialState: {
        isTransferReservation: false,
        transferRoutes: [],
        transferUnits: {},
        isTransferEnabled: false,
        transferSearchResults: [],
        transferPassengers: defaultTransferPassengers,
    },
    reducers: {

        setIsTransferReservation: (state, action) => {
            state.isTransferReservation = action.payload;
        },

        recalculateTransferRoutes: (state, action) => {
            const { hotels = [], services = [] } = action.payload;
            const updatedRoutes = generateTransferRoutes(hotels, services);

            const preservedUnits = {};
            updatedRoutes.forEach(route => {
                if (state.transferUnits[route.routeId]) {
                    preservedUnits[route.routeId] = state.transferUnits[route.routeId];
                }
            });

            state.transferRoutes = updatedRoutes;
            state.transferUnits = preservedUnits;
            state.isTransferReservation = updatedRoutes.length > 0;
            state.transferPassengers = updatedRoutes.length > 0
                ? deriveTransferPassengersFromReservation(hotels, services)
                : defaultTransferPassengers;
        },

        updateTransferRouteFromDate: (state, action) => {
            const { routeId, fromDate } = action.payload;
            const route = state.transferRoutes.find(r => r.routeId === routeId);
            if (route) route.fromDate = fromDate;
        },

        updateTransferRouteToDate: (state, action) => {
            const { routeId, toDate } = action.payload;
            const route = state.transferRoutes.find(r => r.routeId === routeId);
            if (route) route.toDate = toDate;
        },

        updateTransferRouteLocation: (state, action) => {
            const { routeId, field, location } = action.payload;
            const route = state.transferRoutes.find(r => r.routeId === routeId);
            if (route) route[field] = location;
        },

        setTransferRouteDistance: (state, action) => {
            const { routeId, distance, waypointDistances = [] } = action.payload;
            const route = state.transferRoutes.find(r => r.routeId === routeId);
            if (!route) return;
            route.distance = distance;
            if (route.waypoints?.length && waypointDistances.length) {
                route.waypoints.forEach((waypoint, index) => {
                    if (waypointDistances[index] !== undefined) {
                        waypoint.distance = waypointDistances[index];
                    }
                });
            }
        },

        setTransferSearchResults: (state, action) => {
            const { routeId, results } = action.payload;
            const route = state.transferRoutes.find(r => r.routeId === routeId);
            if (route) route.searchResults = results;
        },

        setTransferUnitForRoute: (state, action) => {
            const { routeId, unit } = action.payload;
            state.transferUnits[routeId] = unit;
        },

        clearTransferUnitForRoute: (state, action) => {
            const { routeId } = action.payload;
            delete state.transferUnits[routeId];
        },

        setTransferEnabled: (state, action) => {
            state.isTransferEnabled = action.payload;
        },

        clearAllTransfers: (state) => {
            state.transferRoutes = [];
            state.transferUnits = {};
            state.isTransferReservation = false;
            state.isTransferEnabled = false;
            state.transferPassengers = defaultTransferPassengers;
        },

        setGlobalTransferSearchResults: (state, action) => {
            state.transferSearchResults = action.payload;
        },

        setTransferPassengers: (state, action) => {
            state.transferPassengers = action.payload;
        },
    },
});

export const {
    setIsTransferReservation,
    recalculateTransferRoutes,
    updateTransferRouteFromDate,
    updateTransferRouteToDate,
    updateTransferRouteLocation,
    setTransferRouteDistance,
    setTransferSearchResults,
    setTransferUnitForRoute,
    clearTransferUnitForRoute,
    setTransferEnabled,
    clearAllTransfers,
    setGlobalTransferSearchResults,
    setTransferPassengers
} = ManageTransferSlice.actions;

export const searchTransfers = (instance, accounts, criteria) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.transfers.search(criteria);
    return result;
};

export default ManageTransferSlice.reducer;